import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import SystemShowcase, { HumanGates } from './systemShowcase';

const isVideo = (url) => /\.(mp4|webm)$/i.test(url);
// A video "foo.mp4" uses "foo-poster.jpg" as its first frame while it loads.
const posterFor = (url) => url.replace(/\.(mp4|webm)$/i, '-poster.jpg');

const Project = ({ title, description, imageUrls, date, link, techStack, status, featured, wide, showcase, logo, appStoreUrl, index }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const titleId = `project-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (showModal) {
        if (event.key === 'ArrowLeft') {
          setCurrentImageIndex((prevIndex) =>
            prevIndex > 0 ? prevIndex - 1 : imageUrls.length - 1
          );
        } else if (event.key === 'ArrowRight') {
          setCurrentImageIndex((prevIndex) =>
            prevIndex < imageUrls.length - 1 ? prevIndex + 1 : 0
          );
        } else if (event.key === 'Escape') {
          setShowModal(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal, imageUrls]);

  const openModal = (idx) => {
    setCurrentImageIndex(idx);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <article
      className={[
        'project-card',
        featured && 'project-card--featured',
        showcase && 'project-card--system',
        wide && 'project-card--wide',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-labelledby={titleId}
      style={{
        animationDelay: `${index * 0.1}s`,
      }}
    >
      <div className="project-body">
        <div className="project-header">
          <h3 className={logo ? 'project-title project-title--logo' : 'project-title'} id={titleId}>
            {logo && <img className="project-logo" src={logo} alt="" width="32" height="32" />}
            {link ? (
              <a
                href={link.url}
                target='_blank'
                rel='noopener noreferrer'
              >
                {title}
              </a>
            ) : (
              title
            )}
          </h3>
          <span className="project-date">{date}</span>
        </div>

        {status && <p className="project-status">{status}</p>}

        <p className="project-description">{description}</p>

        {techStack && (
          <div className="tech-stack" aria-label={`${title} technology stack`}>
            {techStack.map((tech) => (
              <span className="tech-pill" key={tech}>
                {tech}
              </span>
            ))}
          </div>
        )}

        {appStoreUrl && (
          <a className="app-store-link" href={appStoreUrl} target="_blank" rel="noopener noreferrer" aria-label={`Download ${title} on the App Store`}>
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.1 12.5c0-2 1.6-3 1.7-3.1-1-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7-.7 0-1.7-.7-2.8-.7-1.5 0-2.9.9-3.6 2.2-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.5 2.1 2.6 2.1 1.1 0 1.5-.7 2.9-.7s1.8.7 3 .7 1.9-1 2.6-2.1c.9-1.2 1.2-2.4 1.2-2.4-.1 0-2.8-1.1-2.8-3.7ZM15.1 6.4c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.8 1.4-.6.7-1.2 1.8-1 2.9 1 .1 2.1-.5 2.8-1.3Z" /></svg>
            <span><small>Download on the</small>App Store</span>
          </a>
        )}

        {showcase && showcase.gates && (
          <HumanGates title={title} gates={showcase.gates} />
        )}
      </div>

      {showcase && <SystemShowcase title={title} showcase={showcase} />}

      {imageUrls && imageUrls.length > 0 && (
        <div className="project-images">
          {imageUrls.map((imageUrl, idx) =>
            isVideo(imageUrl) ? (
              <video
                key={idx}
                src={imageUrl}
                poster={posterFor(imageUrl)}
                aria-label={`${title} preview video`}
                className="project-image"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onClick={() => openModal(idx)}
              />
            ) : (
              <img
                key={idx}
                src={imageUrl}
                alt={`${title} ${idx + 1}`}
                className="project-image"
                onClick={() => openModal(idx)}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )
          )}
        </div>
      )}

      {showModal && ReactDOM.createPortal(
        <div className="image-modal" onClick={closeModal}>
          <button
            onClick={closeModal}
            className="modal-button modal-close"
            aria-label="Close image preview"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {imageUrls.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : imageUrls.length - 1));
                }}
                className="modal-button modal-prev"
                aria-label="Previous image"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev < imageUrls.length - 1 ? prev + 1 : 0));
                }}
                className="modal-button modal-next"
                aria-label="Next image"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </>
          )}

          {isVideo(imageUrls[currentImageIndex]) ? (
            <video
              key={imageUrls[currentImageIndex]}
              src={imageUrls[currentImageIndex]}
              poster={posterFor(imageUrls[currentImageIndex])}
              aria-label={`${title} preview video`}
              className="modal-image"
              autoPlay
              muted
              loop
              playsInline
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={imageUrls[currentImageIndex]}
              alt={`${title} ${currentImageIndex + 1}`}
              className="modal-image"
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {imageUrls.length > 1 && (
            <div className="modal-counter">
              {currentImageIndex + 1} / {imageUrls.length}
            </div>
          )}
        </div>,
        document.body
      )}
    </article>
  );
};

export default Project;
